import { useState } from "react";

import { Drawer, type DrawerBaseProps } from "@/components/Drawer/Drawer";
import { Button } from "@/design-system/Button";
import { FormField } from "@/design-system/FormField";
import { Select } from "@/design-system/Select";
import { Stack } from "@/design-system/Stack";
import { usePortfolioAnalyticsProjectFilterDraft } from "@/modules/analytics/PortfolioAnalyticsProjectFilterFields";
import type {
  CommunityFeedAnalyticsAuthorType,
  CommunityFeedAnalyticsContentType,
  CommunityFeedAnalyticsGroupType,
  CommunityFeedAnalyticsOrigin,
  CommunityFeedAnalyticsStatus,
} from "@/queries/analytics/community-feed-reporting-types";
import { useTranslation } from "@/translations";

export interface CommunityFeedAnalyticsFilterValues {
  authorType: CommunityFeedAnalyticsAuthorType;
  contentType: CommunityFeedAnalyticsContentType;
  groupId: string;
  origin: CommunityFeedAnalyticsOrigin;
  status: CommunityFeedAnalyticsStatus;
}

type CommunityFeedAnalyticsFilterDrawerProps = DrawerBaseProps & {
  filter: CommunityFeedAnalyticsFilterValues;
  groups: {
    id: string;
    name: string;
    type: CommunityFeedAnalyticsGroupType;
  }[];
  showOriginFilter: boolean;
  onUpdate: (filter: CommunityFeedAnalyticsFilterValues) => void;
};

type FilterOption<T> = {
  value: T;
  label: string;
};

export function CommunityFeedAnalyticsFilterDrawer({
  isOpened,
  onOpenChange,
  filter,
  groups,
  showOriginFilter,
  onUpdate,
}: CommunityFeedAnalyticsFilterDrawerProps): React.ReactNode {
  const { t } = useTranslation();

  return (
    <Drawer.Root
      title={t("page.analytics-community-feed.filters.title")}
      isOpened={isOpened}
      onOpenChange={onOpenChange}
      data-testid="community-feed-analytics-filter-drawer"
    >
      {isOpened && (
        <FilterForm filter={filter} groups={groups} showOriginFilter={showOriginFilter} onUpdate={onUpdate} />
      )}
    </Drawer.Root>
  );
}

function FilterForm({
  filter,
  groups,
  showOriginFilter,
  onUpdate,
}: Pick<
  CommunityFeedAnalyticsFilterDrawerProps,
  "filter" | "groups" | "showOriginFilter" | "onUpdate"
>): React.ReactNode {
  const { t } = useTranslation();
  const [draftFilter, setDraftFilter] = useState(filter);
  const portfolioProjectFilter = usePortfolioAnalyticsProjectFilterDraft();
  const contentTypeOptions: FilterOption<CommunityFeedAnalyticsContentType>[] = [
    { value: "all", label: t("page.analytics-community-feed.filters.content.all") },
    { value: "post", label: t("page.analytics-community-feed.filters.content.posts") },
    { value: "announcement", label: t("page.analytics-community-feed.filters.content.announcements") },
    { value: "poll", label: t("page.analytics-community-feed.filters.content.polls") },
  ];
  const authorTypeOptions: FilterOption<CommunityFeedAnalyticsAuthorType>[] = [
    { value: "all", label: t("page.analytics-community-feed.filters.author.all") },
    { value: "resident", label: t("page.analytics-community-feed.filters.author.resident") },
    { value: "projectTeam", label: t("page.analytics-community-feed.filters.author.project-team") },
  ];
  const groupOptions: FilterOption<string>[] = [
    { value: "all", label: t("page.analytics-community-feed.filters.group.all") },
    ...groups.map((group) => ({ value: group.id, label: group.name })),
  ];
  const originOptions: FilterOption<CommunityFeedAnalyticsOrigin>[] = [
    { value: "all", label: t("page.analytics-community-feed.filters.origin.all") },
    { value: "projectOnly", label: t("page.analytics-community-feed.filters.origin.project-only") },
    { value: "projectConnection", label: t("page.analytics-community-feed.filters.origin.project-connection") },
  ];
  const messageStatusOptions: FilterOption<CommunityFeedAnalyticsStatus>[] = [
    { value: "published", label: t("page.analytics-community-feed.filters.status.published") },
    { value: "archived", label: t("page.analytics-community-feed.filters.status.archived") },
  ];
  const pollStatusOptions: FilterOption<CommunityFeedAnalyticsStatus>[] = [
    { value: "open", label: t("page.analytics-community-feed.filters.status.open") },
    { value: "closed", label: t("page.analytics-community-feed.filters.status.closed") },
  ];
  const statusOptions: FilterOption<CommunityFeedAnalyticsStatus>[] = [
    { value: "all", label: t("page.analytics-community-feed.filters.status.all") },
    ...(draftFilter.contentType === "poll" ? [] : messageStatusOptions),
    ...(draftFilter.contentType === "post" || draftFilter.contentType === "announcement" ? [] : pollStatusOptions),
  ];
  const selectedContentType = contentTypeOptions.find((option) => option.value === draftFilter.contentType)!;
  const selectedAuthorType = authorTypeOptions.find((option) => option.value === draftFilter.authorType)!;
  const selectedGroup = groupOptions.find((option) => option.value === draftFilter.groupId) ?? groupOptions[0];
  const selectedOrigin = originOptions.find((option) => option.value === draftFilter.origin)!;
  const selectedStatus = statusOptions.find((option) => option.value === draftFilter.status) ?? statusOptions[0];

  const handleClear = () => {
    const clearedFilter: CommunityFeedAnalyticsFilterValues = {
      authorType: "all",
      contentType: "all",
      groupId: "all",
      origin: "all",
      status: "all",
    };
    setDraftFilter(clearedFilter);
    onUpdate(clearedFilter);
    portfolioProjectFilter.clear();
  };
  const handleApply = () => {
    onUpdate({ ...draftFilter, status: selectedStatus.value });
    portfolioProjectFilter.apply();
  };

  return (
    <>
      <Drawer.Body>
        <Stack gap="md">
          {portfolioProjectFilter.fields}
          <FormField label={t("page.analytics-community-feed.filters.content.label")}>
            <Select
              items={contentTypeOptions}
              selected={selectedContentType}
              onChange={(option) =>
                setDraftFilter((current) => ({ ...current, contentType: option.value, status: "all" }))
              }
              renderOption={(option) => option.label}
              keySelector={(option) => option.value}
            />
          </FormField>
          <FormField label={t("page.analytics-community-feed.filters.author.label")}>
            <Select
              items={authorTypeOptions}
              selected={selectedAuthorType}
              onChange={(option) => setDraftFilter((current) => ({ ...current, authorType: option.value }))}
              renderOption={(option) => option.label}
              keySelector={(option) => option.value}
            />
          </FormField>
          <FormField label={t("page.analytics-community-feed.filters.group.label")}>
            <Select
              items={groupOptions}
              selected={selectedGroup}
              onChange={(option) => setDraftFilter((current) => ({ ...current, groupId: option.value }))}
              renderOption={(option) => option.label}
              keySelector={(option) => option.value}
            />
          </FormField>
          {showOriginFilter && (
            <FormField label={t("page.analytics-community-feed.filters.origin.label")}>
              <Select
                items={originOptions}
                selected={selectedOrigin}
                onChange={(option) => setDraftFilter((current) => ({ ...current, origin: option.value }))}
                renderOption={(option) => option.label}
                keySelector={(option) => option.value}
              />
            </FormField>
          )}
          <FormField label={t("page.analytics-community-feed.filters.status.label")}>
            <Select
              items={statusOptions}
              selected={selectedStatus}
              onChange={(option) => setDraftFilter((current) => ({ ...current, status: option.value }))}
              renderOption={(option) => option.label}
              keySelector={(option) => option.value}
            />
          </FormField>
        </Stack>
      </Drawer.Body>
      <Drawer.Actions>
        <Drawer.Close>
          <Button variant="subtle" onClick={handleClear}>
            {t("common.action.clear")}
          </Button>
        </Drawer.Close>
        <Drawer.Close>
          <Button onClick={handleApply}>{t("common.action.apply")}</Button>
        </Drawer.Close>
      </Drawer.Actions>
    </>
  );
}
